/**
 * WAHA API Service
 *
 * Service per comunicare con WAHA (WhatsApp HTTP API) GOWS engine.
 * Gestisce tutte le chiamate API per messaggi, chat, presenza.
 *
 * Riferimento: MD/WHATSAPP_CHAT_V2_PLAN.md
 */

import type {
  WahaConfig,
  WahaSendResult,
  ChatOverview,
  Message,
  LoadMessagesOptions,
  WahaChatResponse,
  WahaMessageResponse,
  MessageType,
  ACK_STATUS_MAP,
} from '../types/whatsappChat';
import { callWahaProxy } from './wahaProxyService';
import { engineName } from './whatsapp/chatCapabilities';
import { chatDisplayName, inferWahaMessageType, isHiddenSystemChat, isMaskedPhoneName, lastMessagePreviewText } from './whatsapp/chatListRules';
import { buildVcard } from './whatsapp/vcard';
import { wahaReadUpToMs } from './whatsapp/wahaReadState';
import { normalizeWhatsAppDigits } from './whatsapp/phoneDigits';
import { debugLog } from '../../components/whatsapp2/chat/ChatDebugPanel';

class WahaApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message?: string
  ) {
    super(message || `WAHA API Error: ${status} ${statusText}`);
    this.name = 'WahaApiError';
  }
}

/**
 * Service per API WAHA
 */
export class WahaApiService {
  private config: WahaConfig;

  // Engine capabilities — rilevate al primo 501 o tramite session info
  private _engine: string | null = null;
  private _unsupportedFeatures = new Set<string>();

  // Cache contatti globale per risolvere nomi (NOWEB /participants non li restituisce)
  private _contactsCache: Map<string, string> | null = null;
  private _contactsCachePromise: Promise<Map<string, string>> | null = null;
  // Mapping @lid → @c.us estratto dalla lista contatti (campo number)
  private _lidPhoneMap: Map<string, string> | null = null;

  constructor(config: WahaConfig) {
    this.config = config;
  }

  /**
   * Rileva l'engine WAHA dalla session info.
   * Cachea il risultato per tutta la durata del service.
   */
  async detectEngine(): Promise<string | null> {
    if (this._engine) return this._engine;
    try {
      const data = await this.fetch<{ engine?: unknown }>(
        `/api/sessions/${this.config.session}`
      );
      // WAHA manda engine come oggetto ({ engine: 'NOWEB', ... }): si tiene solo il nome.
      const name = engineName(data?.engine);
      if (name) {
        this._engine = name;
        // Matrice ufficiale per motore (https://waha.devlike.pro/docs/how-to/engines/,
        // verificata il 26/09/2026): NOWEB supporta elimina, modifica, fissa, sblocca,
        // stella e inoltra. GOWS non ha fissa/sblocca, stella e inoltra.
        // Per il resto si prova: markUnsupported() scatta al primo 501.
        if (this._engine === 'GOWS') {
          this._unsupportedFeatures.add('pinMessage');
          this._unsupportedFeatures.add('unpinMessage');
          this._unsupportedFeatures.add('starMessage');
          this._unsupportedFeatures.add('forwardMessage');
        }
        console.debug(`[WAHA] Engine rilevato: ${this._engine}`);
      }
      return this._engine;
    } catch {
      return null;
    }
  }

  /** Ritorna true se la feature è supportata dall'engine corrente */
  isFeatureSupported(feature: 'editMessage' | 'deleteMessage' | 'pinMessage' | 'unpinMessage' | 'forwardMessage' | 'starMessage'): boolean {
    return !this._unsupportedFeatures.has(feature);
  }

  /** Motore WAHA gia' rilevato (null finche' detectEngine non ha risposto). */
  getEngine(): string | null {
    return this._engine;
  }

  /** Copia delle funzioni rifiutate dal motore (per decidere cosa mostrare in chat). */
  getUnsupportedFeatures(): string[] {
    return Array.from(this._unsupportedFeatures);
  }

  /** Marca una feature come non supportata (chiamato dopo un 501) */
  private markUnsupported(feature: string): void {
    if (!this._unsupportedFeatures.has(feature)) {
      this._unsupportedFeatures.add(feature);
      console.debug(`[WAHA] Feature "${feature}" non supportata da engine ${this._engine || 'unknown'}`);
    }
  }

  // ============ CHAT ============

  /**
   * Ottiene lista chat (overview)
   */
  async getChatsOverview(limit = 50, offset = 0): Promise<ChatOverview[]> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });

    const response = await this.fetch<WahaChatResponse[]>(
      `/api/${this.config.session}/chats/overview?${params}`
    );

    // Trasforma e ordina per timestamp ultimo messaggio (più recente prima)
    const transformed = response.map(this.transformChat).filter((c) => !isHiddenSystemChat(c.id));

    return transformed.sort((a, b) => {
        // Pinned prima
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        // Poi per timestamp ultimo messaggio (più recente prima)
        const aTime = a.lastMessage?.timestamp || 0;
        const bTime = b.lastMessage?.timestamp || 0;
        return bTime - aTime;
      });
  }

  /**
   * Ottiene tutte le chat per la cache di ricerca.
   * Usa /chats/overview con limit alto ed estrae solo id/name/isGroup.
   */
  async getAllChatsLight(): Promise<Array<{ id: string; name: string; isGroup: boolean }>> {
    const params = new URLSearchParams({
      limit: '3000',
      offset: '0',
    });

    const response = await this.fetch<WahaChatResponse[]>(
      `/api/${this.config.session}/chats/overview?${params}`
    );

    return response.map(chat => {
      const chatId = typeof chat.id === 'string'
        ? chat.id
        : (chat.id?._serialized || chat.id?.user || 'unknown');
      const isGroup = chat.isGroup || chatId.endsWith('@g.us');
      const chatName = (isGroup && chat.groupMetadata?.subject) || chatDisplayName(chat.name, chatId);
      return { id: chatId, name: chatName, isGroup };
    });
  }

  /**
   * Ottiene tutti i contatti WhatsApp per la cache di ricerca.
   * Usa /api/contacts/all (session come query param).
   */
  async getAllContacts(): Promise<Array<{ id: string; name: string; isGroup: false }>> {
    const response = await this.fetch<any[]>(
      `/api/contacts/all?session=${this.config.session}`
    );

    // Costruisci mapping @lid → @c.us dal campo number dei contatti
    const lidPhoneMap = new Map<string, string>();
    for (const contact of response) {
      const cId = typeof contact.id === 'string' ? contact.id : contact.id?._serialized;
      if (cId?.endsWith('@lid') && contact.number) {
        const cusId = contact.number.includes('@') ? contact.number : `${contact.number}@c.us`;
        lidPhoneMap.set(cId, cusId);
      }
    }
    if (lidPhoneMap.size > 0) {
      this._lidPhoneMap = lidPhoneMap;
      console.log(`[WahaService] Built LID→phone map from contacts: ${lidPhoneMap.size} entries`);
    }

    return response
      .filter(contact => {
        const cId = typeof contact.id === 'string' ? contact.id : contact.id?._serialized;
        return cId && !cId.endsWith('@newsletter') && cId !== 'status@broadcast' && !cId.startsWith('120363');
      })
      .map(contact => {
        const id = typeof contact.id === 'string'
          ? contact.id
          : (contact.id?._serialized || 'unknown');
        // I nomi nascosti coi pallini ("+39∙∙∙∙∙∙∙∙28") si saltano: altrimenti rientrano nella
        // lista al posto del numero intero (vedi chatDisplayName).
        const known = [contact.name, contact.pushname, contact.shortName].find((n) => n && !isMaskedPhoneName(n));
        const name = chatDisplayName(known, id);
        return { id, name, isGroup: false as const };
      });
  }

  /**
   * Ritorna il mapping @lid → @c.us costruito da getAllContacts.
   * Disponibile solo dopo aver chiamato getAllContacts/getContactsMap.
   */
  getLidPhoneMap(): Map<string, string> {
    return this._lidPhoneMap || new Map();
  }

  /**
   * Invalida la contacts cache per forzare un reload al prossimo getContactsMap().
   */
  invalidateContactsCache(): void {
    this._contactsCache = null;
    this._contactsCachePromise = null;
  }

  /**
   * Carica tutti i contatti in cache (una sola volta per sessione).
   * Su NOWEB, /participants non restituisce nomi → usiamo contacts/all come fonte.
   * Ritorna Map<id_o_numero, nome>.
   */
  async getContactsMap(): Promise<Map<string, string>> {
    if (this._contactsCache) return this._contactsCache;
    if (this._contactsCachePromise) return this._contactsCachePromise;

    this._contactsCachePromise = (async () => {
      try {
        const contacts = await this.getAllContacts();
        const map = new Map<string, string>();
        for (const c of contacts) {
          map.set(c.id, c.name);
          // Aggiungi anche solo il numero (senza @suffix) per matching flessibile
          const number = c.id.includes('@') ? c.id.split('@')[0] : c.id;
          if (!map.has(number)) map.set(number, c.name);
        }
        this._contactsCache = map;
        console.debug(`[WAHA] Cache contatti caricata: ${map.size} entries`);
        return map;
      } catch (err) {
        console.warn('[WAHA] Errore caricamento cache contatti:', err);
        return new Map<string, string>();
      } finally {
        this._contactsCachePromise = null;
      }
    })();

    return this._contactsCachePromise;
  }

  /**
   * Ottiene tutti i gruppi WhatsApp per la cache di ricerca.
   * Usa /api/{session}/groups con exclude=participants per risposta leggera.
   */
  async getAllGroups(): Promise<Array<{ id: string; name: string; isGroup: true }>> {
    const params = new URLSearchParams({
      limit: '1000',
      offset: '0',
      sortBy: 'subject',
      sortOrder: 'asc',
      exclude: 'participants',
    });

    const rawResponse = await this.fetch<any>(
      `/api/${this.config.session}/groups?${params}`
    );

    // WAHA NOWEB restituisce un oggetto { "jid@g.us": { subject, ... }, ... }
    // invece di un array. Gestiamo entrambi i formati.
    let response: any[];
    if (Array.isArray(rawResponse)) {
      response = rawResponse;
    } else if (rawResponse && typeof rawResponse === 'object') {
      // Oggetto con JID come chiavi → converti in array con id iniettato
      response = Object.entries(rawResponse).map(([key, value]: [string, any]) => ({
        ...value,
        id: value?.id || key,
      }));
    } else {
      response = [];
    }

    return response
      .filter(group => {
        const gId = typeof group.id === 'string' ? group.id : group.id?._serialized;
        return gId && gId.endsWith('@g.us');
      })
      .map(group => {
        const id = typeof group.id === 'string'
          ? group.id
          : (group.id?._serialized || 'unknown');
        const name = group.subject || group.name || id.split('@')[0];
        return { id, name, isGroup: true as const };
      });
  }

  /**
   * Ottiene la lista partecipanti di un gruppo con i loro nomi.
   * Usa /participants/v2 (schema GroupParticipant: { id, pn, role }, id puo' essere @lid e pn
   * e' il @c.us). I nomi non ci sono, quindi incrociamo con la cache contatti da /api/contacts/all.
   * Ritorna Map<numero, nome> (il numero è la parte prima di @): si mettono sia il numero
   * dell'id sia quello di pn, cosi' le menzioni si risolvono in entrambe le forme.
   */
  async getGroupParticipants(groupId: string): Promise<Map<string, string>> {
    try {
      const encodedGroupId = encodeURIComponent(groupId);
      const data = await this.fetch<any[]>(
        `/api/${this.config.session}/groups/${encodedGroupId}/participants/v2`
      );
      const participants = Array.isArray(data) ? data : [];

      const contactsMap = await this.getContactsMap();
      const toNumber = (jid: string) => (jid.includes('@') ? jid.split('@')[0] : jid);

      const map = new Map<string, string>();
      for (const p of participants) {
        const id = typeof p.id === 'string' ? p.id : p.id?._serialized;
        if (!id) continue;
        const pn = typeof p.pn === 'string' && p.pn ? p.pn : undefined;
        const number = toNumber(id);
        const pnNumber = pn ? toNumber(pn) : undefined;

        const name = p.name || p.pushname || p.shortName
          || contactsMap.get(id) || contactsMap.get(number)
          || (pn ? contactsMap.get(pn) || contactsMap.get(pnNumber as string) : undefined);
        if (name) {
          map.set(number, name);
          if (pnNumber && !map.has(pnNumber)) map.set(pnNumber, name);
        }
      }
      return map;
    } catch (err) {
      console.error('[WAHA] getGroupParticipants error for', groupId, ':', err);
      return new Map();
    }
  }

  /**
   * Ottiene foto profilo di un contatto/gruppo
   */
  async getProfilePicture(chatId: string): Promise<string | null> {
    const encodedChatId = encodeURIComponent(chatId);

    const response = await callWahaProxy({
      method: 'GET',
      path: `/api/${this.config.session}/chats/${encodedChatId}/picture`,
    });

    // Errore della richiesta: si lancia, cosi' chi chiama non lo scambia per "nessuna foto"
    // e non lo tiene in memoria fino al prossimo ricaricamento della pagina.
    if (!response.ok) {
      throw new Error(`Foto profilo non letta per ${chatId}: HTTP ${response.status}`);
    }

    const data = await response.json();
    const url = data.profilePictureURL || data.profilePicUrl || data.url || null;
    if (!url) {
      console.debug(`[WAHA] No profile picture for ${chatId} (privacy or not set). Response:`, data);
    }
    return url;
  }

  /**
   * Ottiene info contatto da WAHA (nome, numero, ecc.)
   * Funziona con @c.us, @lid, o numeri di telefono.
   * Specifica WAHA 2026.10: GET /api/contacts?contactId=&session= (prima si chiamava
   * /api/{session}/contacts, che non esiste: 161 risposte 404 al giorno nei registri).
   */
  async getContactInfo(contactId: string): Promise<{ id: string; name?: string; pushname?: string; number?: string } | null> {
    try {
      const params = new URLSearchParams({
        contactId,
        session: this.config.session,
      });

      const response = await callWahaProxy({
        method: 'GET',
        path: `/api/contacts?${params}`,
      });

      if (!response.ok) return null;

      const data = await response.json();
      return {
        id: data.id || contactId,
        name: data.name || data.pushname || data.shortName || undefined,
        pushname: data.pushname || undefined,
        number: data.number || undefined,
      };
    } catch {
      return null;
    }
  }

  // ============ MESSAGGI ============

  /**
   * Ottiene messaggi di una chat
   * Usa offset per paginazione (WAHA restituisce messaggi ordinati per timestamp DESC)
   */
  async getChatMessages(
    chatId: string,
    options: LoadMessagesOptions = {}
  ): Promise<Message[]> {
    const params = new URLSearchParams({
      limit: String(options.limit || 30),
      downloadMedia: String(options.downloadMedia ?? true),
    });

    // Usa offset per paginazione corretta
    if (options.offset !== undefined) {
      params.append('offset', String(options.offset));
    }

    // URL encode il chatId come da documentazione WAHA (@ deve essere %40)
    const encodedChatId = encodeURIComponent(chatId);
    const url = `/api/${this.config.session}/chats/${encodedChatId}/messages?${params}`;

    const response = await this.fetch<WahaMessageResponse[]>(url);

    // Filtra messaggi di sistema
    const SYSTEM_MESSAGE_TYPES = [
      'e2e_notification', 'notification_template', 'protocol',
      'unknown', 'gp2', 'call_log', 'notification',
    ];

    const filtered = response.filter((msg) => {
      const msgType = msg._data?.type || msg.type;
      return !SYSTEM_MESSAGE_TYPES.includes(msgType);
    });

    return filtered.map((msg) => this.transformMessage(msg, chatId));
  }

  // ============ ANTI-BAN ============

  /**
   * Routine anti-ban pre-invio: sendSeen in background (sendSeen è già "umano").
   * Fire-and-forget — NON blocca l'invio del messaggio.
   *
   * Nota: rimossa la chiamata `setPresence(chatId, 'available')` perché WAHA NOWEB
   * NON supporta lo stato 'available' (501 Not Implemented). Su NOWEB la presence
   * outbound supporta solo 'composing' (typing) e 'paused'.
   */
  private antiBanPreSend(chatId: string): void {
    const session = this.config.session;
    (async () => {
      try {
        await callWahaProxy({
          method: 'POST',
          path: `/api/${session}/chats/${encodeURIComponent(chatId)}/messages/read`,
          body: {},
        });
      } catch { /* ignora */ }
    })();
  }

  // ============ INVIO MESSAGGI ============

  /**
   * Normalizza il payload di risposta WAHA per ottenere un WahaSendResult uniforme.
   *
   * WAHA NOWEB ritorna il payload Baileys raw nella forma:
   *   { key: { id }, message: {...}, messageTimestamp: "1775657152", status: "PENDING" }
   * mentre il nostro tipo si aspetta:
   *   { id, timestamp, status: 'sent' | 'queued' }
   *
   * Questo helper gestisce ENTRAMBI i formati (vecchio piatto e nuovo Baileys raw).
   */
  private normalizeSendResult(raw: unknown): WahaSendResult {
    const r = (raw || {}) as Record<string, unknown>;
    const key = r.key as Record<string, unknown> | undefined;

    // ID: prima cerca a root (vecchio formato), poi key.id (Baileys raw)
    const id = (typeof r.id === 'string' && r.id)
      || (typeof key?.id === 'string' && key.id)
      || '';

    // Timestamp: prima a root come number, poi messageTimestamp come string da parsare
    const ts = typeof r.timestamp === 'number'
      ? r.timestamp
      : parseInt(String(r.messageTimestamp || r.timestamp || '0'), 10) || 0;

    // Status: 'sent'/'delivered'/'read' → 'sent', tutto il resto (PENDING/pending/undefined) → 'queued'
    const statusRaw = String(r.status || '').toLowerCase();
    const status: 'sent' | 'queued' = (statusRaw === 'sent' || statusRaw === 'delivered' || statusRaw === 'read')
      ? 'sent'
      : 'queued';

    return { id, timestamp: ts, status };
  }

  /**
   * Invia messaggio di testo
   */
  async sendText(
    chatId: string,
    text: string,
    replyTo?: string,
    mentionedJids?: string[]
  ): Promise<WahaSendResult> {
    this.antiBanPreSend(chatId);

    const body: Record<string, unknown> = {
      chatId,
      text,
      session: this.config.session,
    };

    if (replyTo) {
      body.reply_to = replyTo;
    }

    if (mentionedJids && mentionedJids.length > 0) {
      body.mentions = mentionedJids;
    }

    const raw = await this.fetch<unknown>('/api/sendText', {
      method: 'POST',
      body,
    });
    return this.normalizeSendResult(raw);
  }

  /**
   * Invia immagine
   */
  async sendImage(
    chatId: string,
    file: File,
    caption?: string
  ): Promise<WahaSendResult> {
    this.antiBanPreSend(chatId);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('chatId', chatId);
    formData.append('session', this.config.session);
    if (caption) formData.append('caption', caption);

    const rawImg = await this.fetchMultipart<unknown>('/api/sendImage', formData);
    return this.normalizeSendResult(rawImg);
  }

  /**
   * Invia video
   */
  async sendVideo(
    chatId: string,
    file: File,
    caption?: string
  ): Promise<WahaSendResult> {
    this.antiBanPreSend(chatId);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('chatId', chatId);
    formData.append('session', this.config.session);
    if (caption) formData.append('caption', caption);

    const rawVid = await this.fetchMultipart<unknown>('/api/sendVideo', formData);
    return this.normalizeSendResult(rawVid);
  }

  /**
   * Invia audio/voice note.
   * Schema MessageVoiceRequest: convert e' obbligatorio. Con convert: true WAHA trasforma
   * da se' in Ogg/Opus il formato registrato dal browser (WebM, MP4). Niente didascalia:
   * sendVoice non la prevede.
   */
  async sendVoice(chatId: string, audio: Blob): Promise<WahaSendResult> {
    this.antiBanPreSend(chatId);

    const formData = new FormData();
    const filename = audio instanceof File && audio.name ? audio.name : 'voice';
    formData.append('file', audio, filename);
    formData.append('chatId', chatId);
    formData.append('session', this.config.session);

    const rawVoice = await this.fetchMultipart<unknown>('/api/sendVoice', formData, { convert: true });
    return this.normalizeSendResult(rawVoice);
  }

  /**
   * Invia documento/file generico
   */
  async sendFile(
    chatId: string,
    file: File,
    caption?: string
  ): Promise<WahaSendResult> {
    this.antiBanPreSend(chatId);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('chatId', chatId);
    formData.append('session', this.config.session);
    if (caption) formData.append('caption', caption);

    const rawFile = await this.fetchMultipart<unknown>('/api/sendFile', formData);
    return this.normalizeSendResult(rawFile);
  }

  /**
   * Invia posizione GPS
   */
  async sendLocation(
    chatId: string,
    latitude: number,
    longitude: number,
    title?: string,
    address?: string
  ): Promise<WahaSendResult> {
    this.antiBanPreSend(chatId);
    // Schema MessageLocationRequest: title obbligatorio, address non previsto. Senza titolo si
    // usa l'indirizzo, altrimenti "Posizione".
    const safeTitle = title?.trim() || address?.trim() || 'Posizione';
    return this.fetch<WahaSendResult>('/api/sendLocation', {
      method: 'POST',
      body: { chatId, session: this.config.session, latitude, longitude, title: safeTitle },
    });
  }

  /**
   * Invia contatto vCard.
   * Doc: https://waha.devlike.pro/docs/how-to/send-messages/ "Send contact (vcard)":
   * body { session, chatId, contacts: [{ fullName, organization?, phoneNumber, whatsappId }] }.
   * Prima mandava { name, phone } in cima al body, campi che WAHA non legge.
   */
  async sendContact(
    chatId: string,
    contact: { name: string; phone: string; organization?: string }
  ): Promise<WahaSendResult> {
    this.antiBanPreSend(chatId);
    // Numeri salvati senza prefisso (es. 3345805178) diventano italiani: prima partiva +3345805178.
    const digits = normalizeWhatsAppDigits(contact.phone);
    const phoneNumber = `+${digits}`;
    return this.fetch<WahaSendResult>('/api/sendContactVcard', {
      method: 'POST',
      body: {
        chatId,
        session: this.config.session,
        contacts: [{
          fullName: contact.name,
          ...(contact.organization ? { organization: contact.organization } : {}),
          phoneNumber,
          whatsappId: digits,
          // Biglietto completo scritto da noi: senza il campo N alcuni destinatari (es. numeri
          // Ufficiali Meta) ricevevano il contatto senza nome (prova del 27/09/2026).
          vcard: buildVcard(contact.name, phoneNumber, digits, contact.organization),
        }],
      },
    });
  }

  /**
   * Invia sondaggio
   */
  async sendPoll(
    chatId: string,
    name: string,
    options: string[],
    multipleAnswers = false
  ): Promise<WahaSendResult> {
    this.antiBanPreSend(chatId);
    return this.fetch<WahaSendResult>('/api/sendPoll', {
      method: 'POST',
      body: { chatId, session: this.config.session, poll: { name, options, multipleAnswers } },
    });
  }

  // ============ OPERAZIONI SU MESSAGGI ============
  // Il service lancia WahaApiError(501) se l'engine non supporta l'operazione.

  /**
   * Elimina messaggio per tutti
   */
  async deleteMessage(chatId: string, messageId: string): Promise<void> {
    if (this._unsupportedFeatures.has('deleteMessage')) {
      throw new WahaApiError(501, 'Not Implemented', 'Eliminazione messaggi non supportata da questo engine');
    }
    try {
      const encodedChatId = encodeURIComponent(chatId);
      await this.fetch(
        `/api/${this.config.session}/chats/${encodedChatId}/messages/${encodeURIComponent(messageId)}`,
        { method: 'DELETE' }
      );
    } catch (err) {
      if (err instanceof WahaApiError && err.status === 501) this.markUnsupported('deleteMessage');
      throw err;
    }
  }

  /**
   * Modifica testo di un messaggio inviato
   */
  async editMessage(chatId: string, messageId: string, newText: string): Promise<void> {
    if (this._unsupportedFeatures.has('editMessage')) {
      throw new WahaApiError(501, 'Not Implemented', 'Modifica messaggi non supportata da questo engine');
    }
    try {
      const encodedChatId = encodeURIComponent(chatId);
      await this.fetch(
        `/api/${this.config.session}/chats/${encodedChatId}/messages/${encodeURIComponent(messageId)}`,
        { method: 'PUT', body: { text: newText } }
      );
    } catch (err) {
      if (err instanceof WahaApiError && err.status === 501) this.markUnsupported('editMessage');
      throw err;
    }
  }

  /**
   * Reagisce a un messaggio con emoji
   */
  async reactToMessage(messageId: string, emoji: string): Promise<void> {
    await this.fetch('/api/reaction', {
      method: 'PUT',
      body: { session: this.config.session, messageId, reaction: emoji },
    });
  }

  /**
   * Rimuove la propria reazione da un messaggio (emoji vuota)
   */
  async removeReaction(messageId: string): Promise<void> {
    await this.fetch('/api/reaction', {
      method: 'PUT',
      body: { session: this.config.session, messageId, reaction: '' },
    });
  }

  /**
   * Inoltra messaggio a un'altra chat
   */
  async forwardMessage(messageId: string, toChatId: string): Promise<void> {
    if (this._unsupportedFeatures.has('forwardMessage')) {
      throw new WahaApiError(501, 'Not Implemented', 'Inoltro messaggi non supportato da questo engine');
    }
    try {
      await this.fetch('/api/forwardMessage', {
        method: 'POST',
        body: { session: this.config.session, chatId: toChatId, messageId },
      });
    } catch (err) {
      if (err instanceof WahaApiError && err.status === 501) this.markUnsupported('forwardMessage');
      throw err;
    }
  }

  /**
   * Fissa messaggio in chat (WEBJS e NOWEB; non GOWS)
   */
  async pinMessage(chatId: string, messageId: string, durationSeconds = 604800): Promise<void> {
    if (this._unsupportedFeatures.has('pinMessage')) {
      throw new WahaApiError(501, 'Not Implemented', 'Pin messaggi non supportato da questo engine');
    }
    try {
      const encodedChatId = encodeURIComponent(chatId);
      await this.fetch(
        `/api/${this.config.session}/chats/${encodedChatId}/messages/${encodeURIComponent(messageId)}/pin`,
        { method: 'POST', body: { duration: durationSeconds } }
      );
    } catch (err) {
      if (err instanceof WahaApiError && err.status === 501) this.markUnsupported('pinMessage');
      throw err;
    }
  }

  /**
   * Rimuove messaggio fissato (WEBJS e NOWEB; non GOWS)
   */
  async unpinMessage(chatId: string, messageId: string): Promise<void> {
    if (this._unsupportedFeatures.has('unpinMessage')) {
      throw new WahaApiError(501, 'Not Implemented', 'Unpin messaggi non supportato da questo engine');
    }
    try {
      const encodedChatId = encodeURIComponent(chatId);
      await this.fetch(
        `/api/${this.config.session}/chats/${encodedChatId}/messages/${encodeURIComponent(messageId)}/unpin`,
        { method: 'POST', body: {} }
      );
    } catch (err) {
      if (err instanceof WahaApiError && err.status === 501) this.markUnsupported('unpinMessage');
      throw err;
    }
  }

  /**
   * Aggiunge o toglie la stella (messaggio importante).
   * Doc: https://waha.devlike.pro/docs/how-to/send-messages/ "Star and unstar message":
   * PUT /api/star con { session, chatId, messageId, star }. Non disponibile su GOWS.
   */
  async starMessage(chatId: string, messageId: string, star: boolean): Promise<void> {
    if (this._unsupportedFeatures.has('starMessage')) {
      throw new WahaApiError(501, 'Not Implemented', 'Messaggi importanti non supportati da questo engine');
    }
    try {
      await this.fetch('/api/star', {
        method: 'PUT',
        body: { session: this.config.session, chatId, messageId, star },
      });
    } catch (err) {
      if (err instanceof WahaApiError && err.status === 501) this.markUnsupported('starMessage');
      throw err;
    }
  }

  // ============ PRESENZA ============

  // NOWEB non supporta 'available' presence → dopo il primo 501 su available, skippa solo quello
  private availablePresenceDisabled = false;
  // Se /api/{session}/presence fallisce per typing, usa startTyping/stopTyping come fallback
  private useStartTypingFallback = false;

  /**
   * Imposta stato presenza (typing, recording, online, offline)
   */
  async setPresence(
    chatId: string,
    presence: 'online' | 'offline' | 'typing' | 'recording' | 'paused'
  ): Promise<void> {
    // 'online'/'offline' NON supportati da WAHA NOWEB (rispondono 501 Not Implemented).
    // Skip silenzioso per evitare rumore in console.
    if (presence === 'online' || presence === 'offline') return;

    // Typing/paused: usa fallback startTyping/stopTyping se l'endpoint standard non funziona
    if (this.useStartTypingFallback && (presence === 'typing' || presence === 'paused' || presence === 'recording')) {
      try {
        const endpoint = presence === 'paused'
          ? `/api/stopTyping`
          : `/api/startTyping`;
        await this.fetch(endpoint, {
          method: 'POST',
          body: { chatId, session: this.config.session },
        });
      } catch { /* best effort */ }
      return;
    }

    try {
      await this.fetch(`/api/${this.config.session}/presence`, {
        method: 'POST',
        body: { chatId, presence },
      });
    } catch (err) {
      if (err instanceof WahaApiError && err.status === 501) {
        if (presence === 'typing' || presence === 'paused' || presence === 'recording') {
          // Prova fallback startTyping/stopTyping
          this.useStartTypingFallback = true;
          console.debug('[WAHA] Presence standard 501 per typing — uso startTyping/stopTyping');
          return this.setPresence(chatId, presence);
        } else {
          this.availablePresenceDisabled = true;
          console.debug('[WAHA] Presence "available" non supportato da questo engine — disabilitato');
        }
      }
    }
  }

  /**
   * Ottiene presenza di un contatto
   */
  async getPresence(chatId: string): Promise<{
    isOnline: boolean;
    isTyping: boolean;
    lastSeen?: number;
  }> {
    try {
      // Schema WAHAChatPresences: { id, presences: [{ participant, lastKnownPresence, lastSeen }] }.
      // Prima si leggeva un campo "presence" che non esiste: la presenza iniziale era sempre offline.
      const response = await this.fetch<{
        id: string;
        presences?: Array<{ participant: string; lastKnownPresence: string; lastSeen?: number | null }>;
      }>(`/api/${this.config.session}/presence/${encodeURIComponent(chatId)}`);

      const presences = Array.isArray(response?.presences) ? response.presences : [];
      // Chat 1:1: la voce del contatto (participant = chatId), altrimenti la prima disponibile.
      const entry = presences.find((p) => p.participant === chatId) || presences[0];
      const state = entry?.lastKnownPresence;
      // lastSeen arriva in secondi Unix: chi chiama fa new Date(lastSeen), quindi millisecondi.
      const lastSeen = typeof entry?.lastSeen === 'number' && entry.lastSeen > 0 ? entry.lastSeen * 1000 : undefined;

      return {
        isOnline: state === 'online' || state === 'typing' || state === 'recording',
        isTyping: state === 'typing' || state === 'recording',
        lastSeen,
      };
    } catch {
      return { isOnline: false, isTyping: false };
    }
  }

  /**
   * Risolve un LID (Linked ID) al numero di telefono tramite endpoint dedicato.
   * Es: 150487131263059@lid → 393331234567@c.us
   * Risposta WAHA (schema LidToPhoneNumber): { lid, pn } con pn gia' in forma @c.us.
   * Prima si leggeva phoneNumber, che WAHA non manda: il numero non si risolveva mai.
   */
  async resolveLid(lid: string): Promise<string | null> {
    try {
      const encodedLid = encodeURIComponent(lid.includes('@') ? lid : `${lid}@lid`);
      const data = await this.fetch<{ lid?: string; pn?: string; phoneNumber?: string; number?: string }>(
        `/api/${this.config.session}/lids/${encodedLid}`
      );
      return data?.pn || data?.phoneNumber || data?.number || null;
    } catch {
      return null;
    }
  }

  /**
   * Verifica se un numero di telefono esiste su WhatsApp.
   * Utile prima di inviare messaggi a numeri nuovi (specialmente brasiliani con 9a cifra).
   * GET /api/contacts/check-exists?phone=&session= → { numberExists, chatId, pn? }
   * (doc WAHA: chatId puo' essere @lid, pn e' il @c.us).
   * exists = null quando la verifica non riesce (sessione scollegata, errore di rete):
   * chi chiama decide se chiedere all'utente, invece di dare per buono che esista.
   */
  async checkNumberExists(phone: string): Promise<{ exists: boolean | null; chatId?: string; pn?: string }> {
    try {
      const data = await this.fetch<{ numberExists?: boolean; chatId?: string; pn?: string }>(
        `/api/contacts/check-exists?phone=${encodeURIComponent(phone)}&session=${encodeURIComponent(this.config.session)}`
      );
      if (typeof data?.numberExists !== 'boolean') return { exists: null };
      return { exists: data.numberExists, chatId: data.chatId, pn: data.pn };
    } catch {
      return { exists: null };
    }
  }

  /**
   * Sottoscrive a eventi presenza di un contatto
   */
  async subscribePresence(chatId: string): Promise<void> {
    await this.fetch(`/api/${this.config.session}/presence/${encodeURIComponent(chatId)}/subscribe`, {
      method: 'POST',
    });
  }

  // ============ READ RECEIPTS ============

  /**
   * Marca tutti i messaggi non letti di una chat come letti.
   * Usa POST /api/{session}/chats/{chatId}/messages/read che aggiorna anche l'unreadCount interno WAHA.
   */
  async markAsRead(chatId: string): Promise<void> {
    const encodedChatId = encodeURIComponent(chatId);
    await this.fetch(`/api/${this.config.session}/chats/${encodedChatId}/messages/read`, {
      method: 'POST',
      body: {},
    });
  }

  /**
   * Marca una chat come non letta: POST /api/{session}/chats/{chatId}/unread (unico metodo
   * nella specifica, il vecchio tentativo con PUT non esiste).
   * Best-effort: l'endpoint potrebbe non essere supportato da tutti gli engine WAHA.
   */
  async markAsUnread(chatId: string): Promise<void> {
    const encodedChatId = encodeURIComponent(chatId);
    try {
      await this.fetch(`/api/${this.config.session}/chats/${encodedChatId}/unread`, { method: 'POST', body: {} });
    } catch {
      // Endpoint non supportato da questo engine — UI ottimistica funziona comunque
    }
  }

  // ============ PRIVATE METHODS ============

  private async fetch<T>(
    path: string,
    options?: { method?: string; body?: Record<string, unknown> }
  ): Promise<T> {
    const response = await callWahaProxy({
      method: (options?.method || 'GET') as 'GET' | 'POST' | 'PUT' | 'DELETE',
      path,
      body: options?.body,
    });

    if (!response.ok) {
      throw new WahaApiError(response.status, response.statusText);
    }

    // Gestisci risposte senza body (204 No Content, etc)
    const contentType = response.headers.get('content-type');
    if (response.status === 204 || !contentType?.includes('application/json')) {
      return undefined as T;
    }

    return response.json();
  }

  private async fetchMultipart<T>(path: string, formData: FormData, extra?: Record<string, unknown>): Promise<T> {
    // Converte FormData in JSON con file base64 per il proxy.
    // extra: campi non testuali (es. convert: true) che FormData trasformerebbe in stringa.
    const body: Record<string, any> = { ...(extra || {}) };

    for (const [key, value] of formData.entries()) {
      if (value instanceof File || value instanceof Blob) {
        const arrayBuffer = await value.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);
        // WAHA vuole il base64 puro (doc sendImage): con il prefisso "data:...;base64," la versione
        // 2026.10 legge byte sbagliati e l'immagine arriva bianca e non scaricabile (29/10/2026).
        body.file = {
          data: base64,
          mimetype: value.type || 'application/octet-stream',
          filename: value instanceof File ? value.name : 'file',
        };
      } else {
        body[key] = value;
      }
    }

    const response = await callWahaProxy({
      method: 'POST',
      path,
      body,
    });

    if (!response.ok) {
      throw new WahaApiError(response.status, response.statusText);
    }

    // Gestisci risposte senza body (204 No Content, etc)
    const contentType = response.headers.get('content-type');
    if (response.status === 204 || !contentType?.includes('application/json')) {
      return undefined as T;
    }

    return response.json();
  }

  // ============ TRANSFORMERS ============

  private transformChat = (chat: WahaChatResponse): ChatOverview => {
    // WAHA GOWS può restituire id come stringa o come oggetto
    const chatId = typeof chat.id === 'string'
      ? chat.id
      : (chat.id?._serialized || chat.id?.user || 'unknown');

    // NOWEB potrebbe non settare isGroup per community groups (120363...)
    // Fallback: controlla il suffisso @g.us nel JID
    const isGroup = chat.isGroup || chatId.endsWith('@g.us');

    const chatName = (isGroup && chat.groupMetadata?.subject) ||
      chatDisplayName(chat.name, chatId, this._contactsCache?.get(chatId) || this._contactsCache?.get(chatId.split('@')[0]));

    // unreadCount può essere a livello root o dentro _chat (dipende dal motore WAHA)
    // Se undefined, significa che WAHA non ha il dato (es. GOWS)
    // Se l'ultimo messaggio e' nostro la chat non ha non letti, come su WhatsApp: se si risponde
    // dal CRM il telefono non azzera il suo conteggio e la lista mostrava "2" su chat gia'
    // risposte (caso Mastroianni, 27/09/2026).
    const unreadCount = chat.lastMessage?.fromMe ? 0 : (chat.unreadCount ?? chat._chat?.unreadCount);

    // Estrai sender info per gruppi
    let lastMsgSenderName = chat.lastMessage?.notifyName ||
      chat.lastMessage?._data?.notifyName ||
      chat.lastMessage?.pushName ||
      chat.lastMessage?._data?.pushName;

    // Fallback: cerca nella contacts cache se WAHA NOWEB non popola notifyName/pushName
    if (!lastMsgSenderName && chat.lastMessage?.from && !chat.lastMessage.fromMe) {
      lastMsgSenderName = this._contactsCache?.get(chat.lastMessage.from) ||
        this._contactsCache?.get(chat.lastMessage.from.split('@')[0]);
    }

    // Placeholder per body vuoto su messaggi non-testo (media, sticker, ecc.)
    const lastMsgType = inferWahaMessageType(chat.lastMessage as never);
    const lastMsgBody = chat.lastMessage?.body || (lastMsgType !== 'text' ? lastMessagePreviewText({ body: '', type: lastMsgType }) : '');

    return {
      id: chatId,
      name: chatName,
      isGroup,
      unreadCount,
      pinned: !!(chat as any).pin || !!(chat as any)._chat?.pin || !!(chat as any)._chat?.pinned || false,
      archived: false,
      lastMessage: chat.lastMessage
        ? {
            body: lastMsgBody,
            timestamp: chat.lastMessage.timestamp,
            fromMe: chat.lastMessage.fromMe,
            type: lastMsgType as MessageType,
            senderJid: chat.lastMessage.fromMe ? undefined : chat.lastMessage.from,
            senderName: chat.lastMessage.fromMe ? undefined : lastMsgSenderName,
            // lastMessage di /chats/overview e' un messaggio WAHA: se porta l'ack, spunte vere in lista.
            ...(typeof chat.lastMessage.ack === 'number' ? { ack: chat.lastMessage.ack } : {}),
          }
        : undefined,
      participantsCount: chat.groupMetadata?.participants?.length,
      // Sempre presente (anche undefined): il merge della lista deve poter azzerare il dato precedente
      wahaReadUpToMs: wahaReadUpToMs({ id: chatId, unreadCount, lastMessage: chat.lastMessage ? { timestamp: chat.lastMessage.timestamp, fromMe: chat.lastMessage.fromMe } : undefined }),
    };
  };

  private transformMessage = (
    msg: WahaMessageResponse,
    chatId: string
  ): Message => {
    const ackMap: Record<number, Message['status']> = {
      0: 'pending',
      1: 'sent',
      2: 'delivered',
      3: 'read',
      4: 'played',
    };

    // WAHA può restituire id come stringa o come oggetto con _serialized
    const messageId = typeof msg.id === 'string'
      ? msg.id
      : (msg.id?._serialized || msg.id?.id || `unknown_${msg.timestamp}`);

    // Citazione: WAHA 2026.10 la manda in replyTo {id, body} (schema WAMessage); quotedMsg resta
    // per i motori e le versioni vecchie. Prima si leggeva solo quotedMsg e la citazione si perdeva.
    const replyToId = msg.replyTo?.id
      || (msg.quotedMsg
        ? (typeof msg.quotedMsg.id === 'string'
            ? msg.quotedMsg.id
            : msg.quotedMsg.id?._serialized)
        : undefined);
    const replyToBody = msg.replyTo?.body || msg.quotedMsg?.body || undefined;

    // Per gruppi: il mittente è in author o _data.author (from è l'ID del gruppo)
    // Per chat 1:1: il mittente è in from
    const isGroupChat = chatId.endsWith('@g.us');

    // Helper per estrarre JID (può essere stringa o oggetto con _serialized)
    const extractJid = (val: any): string | undefined => {
      if (!val) return undefined;
      if (typeof val === 'string') return val;
      if (typeof val === 'object' && val._serialized) return val._serialized;
      return undefined;
    };

    const senderJid = msg.fromMe
      ? undefined
      : isGroupChat
        ? (extractJid(msg.author) || extractJid(msg._data?.author) || extractJid(msg.participant) || extractJid(msg._data?.participant) || extractJid(msg._data?.key?.participant))
        : msg.from;

    if (isGroupChat && !msg.fromMe) {
      if (!senderJid) {
        debugLog('WahaApi', `⚠️ Group msg SENZA senderJid: ${messageId?.slice(0,20)}`, {
          chatId,
          'msg.author': msg.author,
          'msg._data?.author': msg._data?.author,
          'msg.participant': msg.participant,
          'msg._data?.participant': msg._data?.participant,
          'msg._data?.key?.participant': msg._data?.key?.participant,
          'msg.from': msg.from,
          dataKeys: msg._data ? Object.keys(msg._data) : [],
        }, 'warn');
      } else {
        debugLog('WahaApi', `✅ senderJid=${senderJid} (source: ${
          extractJid(msg.author) ? 'author' :
          extractJid(msg._data?.author) ? '_data.author' :
          extractJid(msg.participant) ? 'participant' :
          extractJid(msg._data?.participant) ? '_data.participant' :
          extractJid(msg._data?.key?.participant) ? '_data.key.participant' : '?'
        })`);
      }
    }

    // Estrai sender name da notifyName o pushName (per gruppi)
    const senderName = msg.notifyName || msg._data?.notifyName || msg.pushName || msg._data?.pushName || msg._data?.key?.pushName || undefined;

    // WAHA può mettere body/type in _data o in media (stessa logica del webhook)
    const body = msg.body || msg._data?.body || '';
    const rawType = msg._data?.type || msg.type || 'text';
    const mediaUrl = msg.media?.url || msg.mediaUrl;
    const mediaMimetype = msg.media?.mimetype || msg.mimetype;
    const mediaFilename = msg.media?.filename || msg.filename;

    // Normalizza tipo WAHA a tipo interno (allineato con mapMessageType del webhook)
    const normalizeType = (t: string): string => {
      const map: Record<string, string> = {
        chat: 'text', image: 'image', video: 'video', audio: 'audio',
        ptt: 'ptt', document: 'document', sticker: 'sticker',
        location: 'location', vcard: 'contact', poll: 'poll',
      };
      return map[t] || t;
    };
    let normalizedType = normalizeType(rawType);

    // Sticker: type 'text' con mimetype image/webp → sticker (anche senza mediaUrl)
    if (normalizedType === 'text' && mediaMimetype === 'image/webp') {
      normalizedType = 'sticker';
    }

    // NOWEB non manda il tipo: posizione, contatto, sondaggio senza testo risultavano bolle
    // vuote. Si ricava dal messaggio originale, come per la lista chat (27/09/2026).
    if (normalizedType === 'text' && !body) {
      normalizedType = normalizeType(inferWahaMessageType(msg as never));
    }

    // Se il tipo risulta 'text' ma ha media, inferisci dal mimetype
    if (normalizedType === 'text' && mediaUrl && mediaMimetype) {
      if (mediaMimetype.startsWith('image/')) normalizedType = 'image';
      else if (mediaMimetype.startsWith('video/')) normalizedType = 'video';
      else if (mediaMimetype.startsWith('audio/')) normalizedType = 'audio';
      else if (mediaMimetype.startsWith('application/')) normalizedType = 'document';
    }

    return {
      id: messageId,
      chatId,
      content: body,
      type: (normalizedType as MessageType) || 'text',
      direction: msg.fromMe ? 'outbound' : 'inbound',
      status: ackMap[msg.ack] || 'pending',
      timestamp: new Date(msg.timestamp * 1000),
      mediaUrl,
      mediaMimetype,
      mediaFilename,
      replyToId,
      replyToBody,
      senderJid,
      senderName,
      mentionedJidList: msg._data?.mentionedJidList || undefined,
    };
  };
}

// Singleton instance (configurato dopo)
let wahaServiceInstance: WahaApiService | null = null;

/**
 * Inizializza il service WAHA
 */
export function initWahaService(config: WahaConfig): WahaApiService {
  wahaServiceInstance = new WahaApiService(config);
  // Detect engine in background per disabilitare feature non supportate
  wahaServiceInstance.detectEngine();
  return wahaServiceInstance;
}

/**
 * Ottiene l'istanza del service WAHA
 */
export function getWahaService(): WahaApiService {
  if (!wahaServiceInstance) {
    throw new Error('WahaApiService not initialized. Call initWahaService first.');
  }
  return wahaServiceInstance;
}

export { WahaApiError };
